import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Stack,
  Paper,
  Typography,
  Box,
  Alert,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip,
  CircularProgress,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import CloseIcon from '@mui/icons-material/Close';
import GroupComparisonChart from '../charts/GroupComparisonChart';
import { GroupComparisonResponse, PolygonData } from '../../types/schema';
import { NEEDisplayMode } from '../../types/enums';
import { formatArea, formatIntensity, formatNEE } from '../../utils/formatters';
import { getPolygonsByIds } from '../../services/apiClient';
import { useAppSelector } from '../../store/hooks';

interface GroupComparisonModalProps {
  open: boolean;
  onClose: () => void;
  result: GroupComparisonResponse | null;
  loading: boolean;
  error?: string | null;
  annualDisplayMode: NEEDisplayMode;
  monthlyDisplayMode: NEEDisplayMode;
  onAnnualDisplayModeChange: (mode: NEEDisplayMode) => void;
  onMonthlyDisplayModeChange: (mode: NEEDisplayMode) => void;
}

export const GroupComparisonModal: React.FC<GroupComparisonModalProps> = ({
  open,
  onClose,
  result,
  loading,
  error,
  annualDisplayMode,
  monthlyDisplayMode,
  onAnnualDisplayModeChange,
  onMonthlyDisplayModeChange,
}) => {
  const { selectedAccount } = useAppSelector((state) => state.app);
  const [polygonData, setPolygonData] = useState<Record<string, PolygonData[]>>({});
  const [polygonLoading, setPolygonLoading] = useState<Record<string, boolean>>({});

  useEffect(() => {
    if (!result || !open) {
      setPolygonData({});
      setPolygonLoading({});
      return;
    }

    const fetchPolygons = async () => {
      const newPolygonData: Record<string, PolygonData[]> = {};
      const newPolygonLoading: Record<string, boolean> = {};

      for (const group of result.groups) {
        if (group.polygonIds && group.polygonIds.length > 0) {
          newPolygonLoading[group.label] = true;
          try {
            const polygons: PolygonData[] = await getPolygonsByIds(
              group.polygonIds,
              selectedAccount?.accountId
            );
            newPolygonData[group.label] = polygons;
          } catch (err) {
            console.error(`Failed to fetch polygons for ${group.label}:`, err);
            newPolygonData[group.label] = [];
          } finally {
            newPolygonLoading[group.label] = false;
          }
        } else {
          newPolygonData[group.label] = [];
        }
      }

      setPolygonData(newPolygonData);
      setPolygonLoading(newPolygonLoading);
    };

    fetchPolygons();
  }, [result, open, selectedAccount?.accountId]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: {
          height: '90vh',
          maxHeight: '90vh',
        },
      }}
    >
      <DialogTitle>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Group Comparison Results
          </Typography>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Stack>
      </DialogTitle>
      <DialogContent dividers sx={{ overflowY: 'auto', p: 3 }}>
        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}
        {result && (
          <Stack spacing={3}>
            <Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Year Range: {result.startYear} - {result.endYear}
              </Typography>
            </Box>
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
              {result.groups.map((group) => {
                const polygons = polygonData[group.label] || [];
                const isLoadingPolygons = polygonLoading[group.label] || false;

                return (
                  <Paper key={group.label} elevation={1} sx={{ p: 3, flex: 1 }}>
                    <Stack spacing={2}>
                      <Stack spacing={1.5}>
                        <Typography variant="h6" sx={{ fontWeight: 600 }}>
                          {group.label}
                        </Typography>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                          {(group.filters?.tags || []).map(tag => (
                            <Chip key={tag} label={`Tag: ${tag}`} size="small" />
                          ))}
                          {Object.entries(group.filters?.historyFilters || {}).map(([key, values]) =>
                            values.map(value => (
                              <Chip key={`${key}-${value}`} label={`${key}: ${value}`} size="small" />
                            ))
                          )}
                        </Box>
                        <Typography variant="body1">
                          NEE Total: {formatNEE(group.summary.neeTotal ?? undefined, 'tCO2')}
                        </Typography>
                        <Typography variant="body1">
                          Total Area: {formatArea(group.summary.totalArea ?? undefined)}
                        </Typography>
                        <Typography variant="body1">
                          NEE Intensity: {formatIntensity(group.summary.neeIntensity ?? undefined)}
                        </Typography>
                        <Typography variant="body1">
                          Polygons: {group.summary.polygonCount}
                        </Typography>
                      </Stack>

                      {group.polygonIds && group.polygonIds.length > 0 && (
                        <Accordion elevation={0} sx={{ border: '1px solid', borderColor: 'divider' }}>
                          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                              View Polygons ({polygons.length})
                            </Typography>
                          </AccordionSummary>
                          <AccordionDetails>
                            {isLoadingPolygons ? (
                              <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                                <CircularProgress size={24} />
                              </Box>
                            ) : polygons.length > 0 ? (
                              <Box
                                sx={{
                                  display: 'flex',
                                  flexWrap: 'wrap',
                                  gap: 1,
                                  maxHeight: 200,
                                  overflowY: 'auto',
                                }}
                              >
                                {polygons.map((polygon) => (
                                  <Chip
                                    key={polygon.id}
                                    label={polygon.name || `Polygon ${polygon.id}`}
                                    size="small"
                                    variant="outlined"
                                  />
                                ))}
                              </Box>
                            ) : (
                              <Typography variant="body2" color="text.secondary">
                                No polygon details available.
                              </Typography>
                            )}
                          </AccordionDetails>
                        </Accordion>
                      )}
                    </Stack>
                  </Paper>
                );
              })}
            </Stack>
            <GroupComparisonChart
              title="Annual Comparison"
              granularity="annual"
              groups={result.groups}
              displayMode={annualDisplayMode}
              onDisplayModeChange={onAnnualDisplayModeChange}
              loading={loading}
            />
            <GroupComparisonChart
              title="Monthly Comparison"
              granularity="monthly"
              groups={result.groups}
              displayMode={monthlyDisplayMode}
              onDisplayModeChange={onMonthlyDisplayModeChange}
              loading={loading}
            />
          </Stack>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default GroupComparisonModal;
